import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, SkipBack, Maximize2, Minimize2 } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

interface LyricLine {
  time: number;
  endTime: number;
  text: string;
}

interface VisualGuideProps {
  report: any;
  lyricsUrl?: string;
  vocalRecordingUrl?: string;
  leadVocalUrl?: string;
  finalRecordingUrl?: string;
}

const parseLRC = (content: string): LyricLine[] => {
  const lines: LyricLine[] = [];
  const regex = /\[(\d{2}):(\d{2})\.(\d{2,3})\](.*)/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    const centiseconds = match[3].length === 2 ? parseInt(match[3], 10) * 10 : parseInt(match[3], 10);
    const time = minutes * 60 + seconds + centiseconds / 1000;
    let text = match[4].trim();

    text = text.replace(/<\d{2}:\d{2}\.\d{2,3}>/g, '');
    text = text.replace(/\s+/g, ' ').trim();

    if (text) {
      lines.push({ time, endTime: 0, text });
    }
  }

  for (let i = 0; i < lines.length; i++) {
    lines[i].endTime = i < lines.length - 1 ? lines[i + 1].time : lines[i].time + 5;
  }

  return lines;
};

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getDeviationColor = (deviation: number): string => {
  const absDeviation = Math.abs(deviation);
  if (absDeviation < 0.15) return '#22c55e';
  if (absDeviation < 0.3) return '#eab308';
  if (absDeviation < 0.5) return '#f97316';
  return '#ef4444';
};

const getDeviationLabel = (deviation: number): string => {
  if (deviation > 0.05) return 'Sharp';
  if (deviation < -0.05) return 'Flat';
  return 'On Pitch';
};

export const VisualGuide: React.FC<VisualGuideProps> = ({
  report,
  lyricsUrl,
  vocalRecordingUrl,
  leadVocalUrl,
  finalRecordingUrl
}) => {
  const [lyrics, setLyrics] = useState<LyricLine[]>([]);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentDeviation, setCurrentDeviation] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lyricsContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const { results = {}, pitch_curves = {} } = report || {};
  const { anomaly_curve = [], time_points = [] } = results || {};
  const { reference = [], recording = [] } = pitch_curves || {};
  const totalDuration = time_points?.length ? time_points[time_points.length - 1] : 0;
  const hasTimeData = time_points.length > 0 && anomaly_curve.length > 0;
  const hasPitchCurves = reference.length > 0 && recording.length > 0;

  useEffect(() => {
    if (!lyricsUrl) return;

    const fetchLyrics = async () => {
      setLyricsLoading(true);
      try {
        const response = await fetch(lyricsUrl);
        if (!response.ok) throw new Error('Failed to fetch lyrics');
        const text = await response.text();
        const parsed = parseLRC(text);
        setLyrics(parsed);
      } catch (err) {
        console.error('Error fetching lyrics:', err);
      } finally {
        setLyricsLoading(false);
      }
    };

    fetchLyrics();
  }, [lyricsUrl]);

  const getDeviationAtTime = useCallback((time: number): number => {
    if (!time_points.length || !anomaly_curve.length) return 0;

    let closestIdx = 0;
    let minDiff = Math.abs(time_points[0] - time);

    for (let i = 1; i < time_points.length; i++) {
      const diff = Math.abs(time_points[i] - time);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = i;
      }
    }

    return anomaly_curve[closestIdx] || 0;
  }, [time_points, anomaly_curve]);

  const getCurrentLyricIndex = useCallback((time: number): number => {
    for (let i = 0; i < lyrics.length; i++) {
      if (time >= lyrics[i].time && time < lyrics[i].endTime) {
        return i;
      }
    }
    return -1;
  }, [lyrics]);

  const getDeviationForLine = useCallback((lineIndex: number): number => {
    if (lineIndex < 0 || lineIndex >= lyrics.length) return 0;
    const line = lyrics[lineIndex];

    let sum = 0;
    let count = 0;
    for (let i = 0; i < time_points.length; i++) {
      if (time_points[i] >= line.time && time_points[i] < line.endTime) {
        sum += anomaly_curve[i] || 0;
        count++;
      }
    }

    return count > 0 ? sum / count : 0;
  }, [lyrics, time_points, anomaly_curve]);

  const chartData = useMemo(() => {
    if (!totalDuration) return null;

    const datasets: any[] = [];

    if (hasPitchCurves) {
      const sampledRef: { x: number; y: number }[] = [];
      const sampledRec: { x: number; y: number }[] = [];
      const sampleRate = Math.max(1, Math.floor(reference.length / 500));

      for (let i = 0; i < reference.length; i += sampleRate) {
        const time = (i / reference.length) * totalDuration;
        if (reference[i] > 0) {
          sampledRef.push({ x: time, y: reference[i] });
        }
        if (recording[i] > 0) {
          sampledRec.push({ x: time, y: recording[i] });
        }
      }

      datasets.push(
        {
          label: 'Original',
          data: sampledRef,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.1,
        },
        {
          label: 'Your Recording',
          data: sampledRec,
          borderColor: '#f97316',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.1,
        }
      );
    } else if (hasTimeData) {
      const sampleRate = Math.max(1, Math.floor(anomaly_curve.length / 500));
      const targetPoints: { x: number; y: number }[] = [];
      const deviationPoints: { x: number; y: number }[] = [];

      for (let i = 0; i < anomaly_curve.length; i += sampleRate) {
        const time = time_points[i];
        targetPoints.push({ x: time, y: 0 });
        deviationPoints.push({ x: time, y: anomaly_curve[i] });
      }

      datasets.push(
        {
          label: 'Target (Perfect Pitch)',
          data: targetPoints,
          borderColor: '#22c55e',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          borderDash: [5, 5],
          fill: false,
          tension: 0,
        },
        {
          label: 'Your Recording',
          data: deviationPoints,
          borderColor: '#f97316',
          backgroundColor: 'rgba(249, 115, 22, 0.15)',
          borderWidth: 2,
          pointRadius: 0,
          fill: 'origin',
          tension: 0.1,
        }
      );
    }

    return datasets.length > 0 ? { datasets } : null;
  }, [reference, recording, totalDuration, hasPitchCurves, hasTimeData, anomaly_curve, time_points]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    parsing: false as const,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: { size: 11 },
          boxWidth: 12,
          padding: 8,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        type: 'linear' as const,
        min: 0,
        max: totalDuration,
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: { size: 10 },
          callback: (value: number | string) => formatTime(Number(value)),
          maxTicksLimit: 10,
        },
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: { size: 10 },
          callback: (value: number | string) => hasPitchCurves ? `${Number(value).toFixed(0)} Hz` : Number(value).toFixed(2),
        },
        title: {
          display: !hasPitchCurves,
          text: 'Deviation (- Flat / + Sharp)',
          color: 'rgba(255, 255, 255, 0.5)',
          font: { size: 10 },
        },
      },
    },
  }), [totalDuration, hasPitchCurves]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const time = audio.currentTime;
      setCurrentTime(time);
      setCurrentDeviation(getDeviationAtTime(time));
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [getDeviationAtTime]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const restart = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    if (!isPlaying) {
      audio.play();
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(!isMuted);
  };

  const seekTo = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const newTime = percent * totalDuration;

    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const currentLyricIndex = getCurrentLyricIndex(currentTime);
  const audioSrc = finalRecordingUrl || vocalRecordingUrl || leadVocalUrl;

  console.log('[VisualGuide] Audio sources:', {
    finalRecordingUrl,
    vocalRecordingUrl,
    leadVocalUrl,
    selectedAudioSrc: audioSrc
  });

  if (!report || (!hasTimeData && !hasPitchCurves)) {
    return (
      <div className="flex items-center justify-center h-full text-white/50">
        No pitch data available for visualization
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-[#1a1a2e] ${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'}`}
    >
      <audio
        ref={audioRef}
        src={audioSrc || ''}
        preload="metadata"
      />

      <div className="flex items-center gap-4 px-4 py-3 bg-[#16162a] border-b border-white/10">
        <div className="text-sm text-white/70 font-mono min-w-[100px]">
          {formatTime(currentTime)} / {formatTime(totalDuration)}
        </div>

        <div
          className="flex-1 h-2 bg-[#2a2a4a] rounded-full cursor-pointer relative overflow-hidden"
          onClick={seekTo}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-100"
            style={{
              width: `${(currentTime / totalDuration) * 100}%`,
              backgroundColor: getDeviationColor(currentDeviation)
            }}
          />
        </div>

        <div
          className="px-3 py-1 rounded text-sm font-medium min-w-[120px] text-center"
          style={{
            backgroundColor: `${getDeviationColor(currentDeviation)}20`,
            color: getDeviationColor(currentDeviation),
            border: `1px solid ${getDeviationColor(currentDeviation)}40`
          }}
        >
          {currentDeviation > 0 ? '+' : ''}{currentDeviation.toFixed(2)} {getDeviationLabel(currentDeviation)}
        </div>
      </div>

      <div className="flex-1 relative min-h-0">
        {chartData && (
          <div className="absolute inset-0 p-4">
            <Line data={chartData} options={chartOptions} />

            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white/80 pointer-events-none z-10"
              style={{
                left: `calc(${(currentTime / totalDuration) * 100}% + 48px)`,
                transition: 'left 0.1s linear'
              }}
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rounded-full" />
            </div>
          </div>
        )}
      </div>

      <div className="h-20 bg-[#16162a] border-t border-white/10 overflow-hidden relative flex items-center">
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-white/30 pointer-events-none z-10" />

        <div
          ref={lyricsContainerRef}
          className="flex items-center gap-8 px-8 transition-transform duration-150 ease-linear"
          style={{
            transform: `translateX(calc(50vw - ${currentLyricIndex >= 0 ? currentLyricIndex * 200 + 100 : 0}px))`
          }}
        >
          {lyrics.map((line, index) => {
            const lineDeviation = getDeviationForLine(index);
            const isActive = index === currentLyricIndex;
            const isPast = currentTime > line.endTime;

            let color = 'rgba(255, 255, 255, 0.3)';
            if (isActive) {
              color = getDeviationColor(currentDeviation);
            } else if (isPast) {
              color = getDeviationColor(lineDeviation);
            }

            return (
              <span
                key={index}
                className={`whitespace-nowrap transition-all duration-200 ${
                  isActive ? 'text-lg font-bold' : 'text-sm'
                }`}
                style={{
                  color: color,
                  textShadow: isActive ? `0 0 20px ${color}` : 'none',
                  opacity: isActive ? 1 : isPast ? 0.7 : 0.4,
                  minWidth: 'max-content',
                }}
              >
                {line.text}
              </span>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 px-4 py-3 bg-[#16162a] border-t border-white/10">
        <button
          onClick={restart}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Restart"
        >
          <SkipBack size={20} />
        </button>

        <button
          onClick={togglePlayback}
          className="p-3 rounded-full bg-[#22c55e] hover:bg-[#16a34a] text-white transition-colors"
          disabled={!audioSrc}
        >
          {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />}
        </button>

        <button
          onClick={toggleMute}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>

        <div className="mx-4 h-6 w-px bg-white/20" />

        <button
          onClick={toggleFullscreen}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
        </button>
      </div>

      {lyricsLoading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}
    </div>
  );
};
